<template>
  <div
    ref="tabsRef"
    class="ui-tabs"
  >
    <nav
      class="ui-tabs__wrapper"
      aria-label="Tabs"
      :class="wrapperClasses"
    >
      <template
        v-for="tab in tabs"
        :key="tab.name"
      >
        <router-link
          v-if="tab.route"
          :to="tab.route"
          :disabled
          exact
          exact-active-class="isActive"
          class="ui-tabs__item"
          :class="{ 'isSqueezed': isSqueezed }"
        >
          <slot :tab-name="tab.name">
            <component
              :is="tab.icon"
              v-if="tab.icon"
              aria-hidden="true"
            />
            <span>{{ tab.name }}</span>
          </slot>
        </router-link>
        <div
          v-else
          class="ui-tabs__item"
          :class="{ 'isActive': modelValue === tab.name, 'isSqueezed': isSqueezed }"
          @click="handleClick(tab.name)"
        >
          <slot :tab-name="tab.name">
            <component
              :is="tab.icon"
              v-if="tab.icon"
              aria-hidden="true"
            />
            <span>{{ tab.name }}</span>
          </slot>
        </div>
      </template>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'

import { TabsItem } from './utils/types'
import { useElementSize } from '@vueuse/core'

type Props = {
  tabs: TabsItem[]
  disabled?: boolean
  squeezeWidth?: number
}

const props = defineProps<Props>()

const modelValue = defineModel<string>()

const tabsRef = useTemplateRef('tabsRef')

const { width } = useElementSize(tabsRef)

const wrapperClasses = computed(() => ({
  'isDisabled': props.disabled,
}))

const isSqueezed = computed(() => !!props.squeezeWidth && (width.value <= props.squeezeWidth))

const handleClick = (name: string) => {
  if (props.disabled) return
  modelValue.value = name
}
</script>

<style>
.ui-tabs {
  @apply w-full;
  @apply shrink-0;
  @apply bg-body-dark;
  @apply rounded-lg;
  @apply overflow-x-auto;

  &__wrapper {
    @apply flex items-center gap-x-1;
    @apply p-1;

    .isDisabled {
      @apply opacity-50;
      @apply cursor-not-allowed;
      @apply pointer-events-none;
    }
  }

  &__item {
    @apply min-h-8;
    @apply flex items-center justify-center gap-1.5;
    @apply flex-auto;
    @apply py-2 px-2.5;
    @apply text-xs;
    @apply rounded-md;
    @apply hover:text-primary dark:hover:text-indigo-300;
    @apply font-medium uppercase;
    @apply text-center;
    @apply whitespace-nowrap;
    @apply cursor-pointer;

    &.isActive {
      @apply bg-white! dark:bg-body!;
      @apply shadow-[0_0_0.25rem_rgba(0,0,0,0.05)];
      @apply text-common-text!;
    }

    svg {
      @apply size-4.5;
      @apply -my-1;
      @apply opacity-50;
    }

    .isSqueezed {
      @apply flex-col gap-2;
      @apply text-[.675rem] leading-tight;

      svg {
        @apply size-6;
      }
    }
  }
}
</style>
